import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PenLine, Mail, DoorOpen, FileWarning, Plus, ArrowRight, ClipboardList, Search, X, Upload, ListTodo, MailX, MailQuestion, RefreshCw, KeyRound } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const TYPE_LABEL = { entree: "Entrée", sortie: "Sortie", entree_sortie: "Entrée + sortie" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-CH") : "—");

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [gerant, setGerant] = useState("all");
  const [immeuble, setImmeuble] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = {};
    if (gerant !== "all") params.gerant = gerant;
    if (q.trim()) params.immeuble = q.trim();
    api.get("/dashboard", { params }).then((r) => setData(r.data)).catch(() => setData({ stats: {}, a_signer: [], a_envoyer: [], sorties_a_planifier: [], avis_defauts: [], filtres: { gerants: [], immeubles: [] } }));
  }, [gerant, q]);

  useEffect(() => { const t = setTimeout(() => setQ(immeuble), 400); return () => clearTimeout(t); }, [immeuble]);

  if (!data) return <div className="text-dd-warm text-sm" data-testid="dashboard-loading">Chargement…</div>;
  const s = data.stats || {};

  return (
    <div data-testid="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-dd-blue">Bonjour {user?.name?.split(" ")[0] || ""}</h1>
          <p className="text-sm text-dd-warm mt-1">Voici ce qui vous attend aujourd'hui.</p>
        </div>
        <Button onClick={() => navigate("/constats/nouveau")} className="h-11 bg-dd-blue hover:bg-dd-hover text-white" data-testid="dash-new-constat">
          <Plus size={18} className="mr-1" /> Nouveau constat
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="dash-filters">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-dd-warm" />
          <Input value={immeuble} onChange={(e) => setImmeuble(e.target.value)} placeholder="Filtrer par immeuble, localité ou dossier…" className="h-11 pl-9" data-testid="dash-filter-immeuble" list="dash-immeubles" />
          <datalist id="dash-immeubles">{(data.filtres?.immeubles || []).map((i) => <option key={i} value={i} />)}</datalist>
        </div>
        <Select value={gerant} onValueChange={setGerant}>
          <SelectTrigger className="h-11 w-[240px]" data-testid="dash-filter-gerant"><SelectValue placeholder="Tous les gérants" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les gérants</SelectItem>
            {(data.filtres?.gerants || []).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        {(gerant !== "all" || immeuble) && (
          <Button variant="ghost" onClick={() => { setGerant("all"); setImmeuble(""); }} className="h-11 text-dd-blue" data-testid="dash-filter-reset"><X size={16} className="mr-1" /> Effacer les filtres</Button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Constats ce mois" value={s.ce_mois} testid="stat-mois" />
        <Stat label="En cours" value={s.en_cours} testid="stat-en-cours" />
        <Stat label="Signés" value={s.signes} testid="stat-signes" />
        <Stat label="Total" value={s.total} testid="stat-total" />
      </div>

      {data.imports?.total > 0 && (
        <div className="bg-white rounded-lg border border-dd-light mb-6" data-testid="panel-imports">
          <div className="flex items-center justify-between px-5 py-3 border-b border-dd-light">
            <div className="flex items-center gap-2 text-dd-blue font-semibold"><Upload size={18} /> Constats importés</div>
            <div className="flex gap-2 text-xs">
              <span className="rounded-full px-2.5 py-0.5 bg-dd-light/60 text-dd-night font-semibold" data-testid="imports-total">{data.imports.total} importés</span>
              <span className="rounded-full px-2.5 py-0.5 bg-green-100 text-green-800 font-semibold" data-testid="imports-relus">{data.imports.relus} relus</span>
              <span className="rounded-full px-2.5 py-0.5 bg-amber-100 text-amber-900 font-semibold" data-testid="imports-a-relire">{data.imports.a_relire} à relire</span>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-dd-warm"><th className="px-5 py-2 font-medium">Gérant</th><th className="px-3 py-2 font-medium text-right">Importés</th><th className="px-3 py-2 font-medium text-right">Relus</th><th className="px-5 py-2 font-medium text-right">À relire</th></tr></thead>
            <tbody className="divide-y divide-dd-light">
              {data.imports.par_gerant.map((r) => (
                <tr key={r.gerant} data-testid={`imports-gerant-${r.gerant}`} className="hover:bg-dd-light/40 cursor-pointer" onClick={() => navigate("/constats?importe=1")}>
                  <td className="px-5 py-2.5 text-dd-night font-medium">{r.gerant}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{r.importes}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-green-700">{r.relus}</td>
                  <td className={`px-5 py-2.5 text-right tabular-nums font-semibold ${r.a_relire ? "text-amber-700" : "text-dd-warm"}`}>{r.a_relire}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ActionsPanel actions={data.actions || []} onDone={(a) => setData((d) => ({ ...d, actions: d.actions.filter((x) => x.action_id !== a.action_id) }))} />
        <SuiviEnvoisPanel relances={data.relances || []} rebonds={data.rebonds || []} onRenvoye={(r) => setData((d) => ({ ...d, relances: d.relances.filter((x) => !(x.etat_id === r.etat_id && x.email === r.email)) }))} />
        <ClesSortiesPanel items={data.cles_sorties || []} />
        <Panel icon={PenLine} title="À finaliser et signer" items={data.a_signer} testid="panel-a-signer"
          empty="Aucun constat en cours. Créez un constat pour commencer."
          render={(c) => <>{c.obs_incompletes > 0 && <span className="text-xs text-destructive font-medium">{c.obs_incompletes} obs. sans commentaire</span>}</>} />
        <Panel icon={Mail} title="Signés, PDF non envoyé aux parties" items={data.a_envoyer} testid="panel-a-envoyer"
          empty="Tous les constats signés ont été transmis." />
        <Panel icon={DoorOpen} title="Sorties à planifier" items={data.sorties_a_planifier} testid="panel-sorties"
          empty="Aucune entrée signée sans constat de sortie."
          hint="Entrées signées sans constat de sortie : ouvrez l'entrée puis « Créer le constat de sortie »." />
        <Panel icon={FileWarning} title="Avis des défauts à envoyer" items={data.avis_defauts} testid="panel-avis"
          empty="Aucun avis des défauts en attente."
          hint="Sorties signées avec défauts à charge du locataire : lettre recommandée à générer et à envoyer par e-mail (CO 267a)." />
      </div>
    </div>
  );
}

function ActionsPanel({ actions, onDone }) {
  const navigate = useNavigate();
  const [resp, setResp] = useState("all");
  const RESP = { bailleur: "Bailleur", locataire: "Locataire", gerance: "Gérance" };
  const list = actions.filter((a) => resp === "all" || a.responsable === resp);
  const done = async (a) => {
    try { await api.post(`/etats/${a.etat_id}/actions/${a.action_id}`, { fait: true }); onDone(a); toast.success("Action marquée comme faite"); }
    catch (e) { toast.error("Impossible de mettre à jour l'action"); }
  };
  return (
    <div className="bg-white rounded-lg border border-dd-light xl:col-span-2" data-testid="panel-actions">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-dd-light">
        <div className="flex items-center gap-2 text-dd-blue font-semibold"><ListTodo size={18} /> Actions à entreprendre
          <span className="text-sm font-semibold text-dd-night bg-dd-light/60 rounded-full px-2.5 py-0.5">{list.length}</span></div>
        <Select value={resp} onValueChange={setResp}>
          <SelectTrigger className="h-9 w-44 bg-white" data-testid="actions-filter-resp"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les responsables</SelectItem>
            {Object.entries(RESP).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {list.length === 0 ? <p className="px-5 py-5 text-sm text-dd-warm">Aucune action en attente. Tout est à jour.</p> : (
        <div className="divide-y divide-dd-light">
          {list.map((a) => (
            <div key={a.action_id} className="px-5 py-2.5 flex items-center gap-3 min-h-[52px]" data-testid={`dash-action-${a.action_id}`}>
              <Checkbox onCheckedChange={(v) => v && done(a)} data-testid={`dash-action-fait-${a.action_id}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-dd-night truncate">{a.texte}</div>
                <div className="text-xs text-dd-warm truncate">{a.immeuble || "—"}{a.numero_objet ? ` · ${a.numero_objet}` : ""}{a.numero_dossier ? ` · ${a.numero_dossier}` : ""} · {fmt(a.date_constat)}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${a.responsable === "locataire" ? "bg-amber-100 text-amber-900" : a.responsable === "gerance" ? "bg-dd-light text-dd-night" : "bg-dd-blue/10 text-dd-blue"}`}>{RESP[a.responsable] || a.responsable}</span>
              <button onClick={() => navigate(`/constats/${a.etat_id}`)} className="text-dd-blue shrink-0 h-9 w-9 flex items-center justify-center" data-testid={`dash-action-open-${a.action_id}`}><ArrowRight size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SuiviEnvoisPanel({ relances, rebonds, onRenvoye }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  if (!relances.length && !rebonds.length) return null;
  const renvoyer = async (r) => {
    setBusy(r.etat_id + r.email);
    try { await api.post(`/etats/${r.etat_id}/envois/renvoyer`, { destinataire: r.destinataire, email: r.email }); toast.success(`PV renvoyé à ${r.email}`); onRenvoye(r); }
    catch (e) { toast.error("Renvoi impossible"); } finally { setBusy(null); }
  };
  const Row = ({ r, children }) => (
    <div className="px-5 py-2.5 flex items-center gap-3 min-h-[52px]" data-testid={`suivi-${r.etat_id}-${r.email}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-dd-night truncate">{r.email} <span className="text-dd-warm">· {r.destinataire.replace(/_/g, " ")}</span></div>
        <div className="text-xs text-dd-warm truncate">{r.immeuble || "—"}{r.numero_objet ? ` · ${r.numero_objet}` : ""}{r.numero_dossier ? ` · ${r.numero_dossier}` : ""} · envoyé le {fmt(r.date)}</div>
      </div>
      {children}
    </div>
  );
  return (
    <div className="bg-white rounded-lg border border-dd-light xl:col-span-2" data-testid="panel-suivi-envois">
      {rebonds.length > 0 && (
        <>
          <div className="flex items-center gap-2 px-5 py-3 border-b border-dd-light text-destructive font-semibold"><MailX size={18} /> E-mails en échec
            <span className="text-sm font-semibold bg-destructive/10 rounded-full px-2.5 py-0.5">{rebonds.length}</span>
            <span className="text-xs font-normal text-dd-warm ml-2">Adresse invalide ou refusée — corrigez-la dans le constat puis renvoyez.</span></div>
          <div className="divide-y divide-dd-light">
            {rebonds.map((r) => (
              <Row key={r.etat_id + r.email} r={r}>
                <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/10 text-destructive shrink-0">{r.statut === "spam" ? "Marqué spam" : r.statut === "rebond" ? "Rebond" : "Échec"}</span>
                <Button size="sm" variant="outline" onClick={() => navigate(`/constats/${r.etat_id}`)} className="h-9 border-dd-blue text-dd-blue" data-testid={`corriger-${r.etat_id}`}>Corriger l'adresse</Button>
              </Row>
            ))}
          </div>
        </>
      )}
      {relances.length > 0 && (
        <>
          <div className="flex items-center gap-2 px-5 py-3 border-b border-t border-dd-light text-dd-blue font-semibold"><MailQuestion size={18} /> À relancer
            <span className="text-sm font-semibold text-dd-night bg-dd-light/60 rounded-full px-2.5 py-0.5">{relances.length}</span>
            <span className="text-xs font-normal text-dd-warm ml-2">PV non ouvert par le locataire depuis plus de 7 jours.</span></div>
          <div className="divide-y divide-dd-light">
            {relances.map((r) => (
              <Row key={r.etat_id + r.email} r={r}>
                <Button size="sm" onClick={() => renvoyer(r)} disabled={busy === r.etat_id + r.email} className="h-9 bg-dd-blue text-white hover:bg-dd-night" data-testid={`renvoyer-${r.etat_id}`}>
                  <RefreshCw size={14} className={`mr-1 ${busy === r.etat_id + r.email ? "animate-spin" : ""}`} /> Renvoyer
                </Button>
              </Row>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ClesSortiesPanel({ items }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  if (!items.length) return null;
  const relancer = async (r) => {
    setBusy(r.id);
    try { await api.post(`/remises/${r.id}/relancer`); toast.success(`Rappel envoyé à ${r.email}`); } catch (e) { toast.error("Relance impossible (e-mail de l'entreprise manquant ?)"); } finally { setBusy(null); }
  };
  const retards = items.filter((i) => i.en_retard).length;
  return (
    <div className="bg-white rounded-lg border border-dd-light xl:col-span-2" data-testid="panel-cles-sorties">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-dd-light text-dd-blue font-semibold"><KeyRound size={18} /> Clés non restituées
        <span className="text-sm font-semibold text-dd-night bg-dd-light/60 rounded-full px-2.5 py-0.5">{items.length}</span>
        {retards > 0 && <span className="text-xs font-semibold text-destructive bg-destructive/10 rounded-full px-2.5 py-0.5">{retards} en retard</span>}</div>
      <div className="divide-y divide-dd-light">
        {items.map((r) => (
          <div key={r.id} className="px-5 py-2.5 flex items-center gap-3 min-h-[52px]" data-testid={`cle-sortie-${r.id}`}>
            <div className="flex-1 min-w-0"><div className="text-sm text-dd-night truncate">{r.entreprise || "—"} <span className="text-dd-warm">· {r.employe}</span></div>
              <div className="text-xs text-dd-warm truncate">{r.immeuble} · {r.nb_cles} clé{r.nb_cles > 1 ? "s" : ""} · retour prévu {fmt(r.retour_prevu)}{r.derniere_relance ? ` · relancé le ${fmt(r.derniere_relance)}` : ""}</div></div>
            {r.en_retard && <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/10 text-destructive shrink-0">En retard</span>}
            {r.en_retard && r.email && <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => relancer(r)} className="h-9 border-dd-blue text-dd-blue" data-testid={`relancer-cle-${r.id}`}><Mail size={14} className="mr-1" /> Relancer</Button>}
            <button onClick={() => navigate(`/cles/${r.id}`)} className="text-dd-blue shrink-0 h-9 w-9 flex items-center justify-center" data-testid={`ouvrir-cle-${r.id}`}><ArrowRight size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Panel({ icon: Icon, title, items, empty, hint, render, testid }) {
  const navigate = useNavigate();
  return (
    <div className="bg-white rounded-lg border border-dd-light" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-dd-light">
        <div className="flex items-center gap-2 text-dd-blue font-semibold"><Icon size={18} /> {title}</div>
        <span className="text-sm font-semibold text-dd-night bg-dd-light/60 rounded-full px-2.5 py-0.5">{items.length}</span>
      </div>
      {hint && <p className="px-5 pt-3 text-xs text-dd-warm">{hint}</p>}
      {items.length === 0 ? <p className="px-5 py-5 text-sm text-dd-warm">{empty}</p> : (
        <div className="divide-y divide-dd-light">
          {items.map((c) => (
            <button key={c.id} onClick={() => navigate(`/constats/${c.id}`)} data-testid={`dash-item-${c.id}`}
              className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-dd-light/40 min-h-[56px]">
              <ClipboardList size={16} className="text-dd-warm shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-dd-night truncate">{c.immeuble || "Immeuble non renseigné"}{c.numero_objet ? ` · ${c.numero_objet}` : ""}{c.localite ? ` · ${c.localite}` : ""}</div>
                <div className="text-xs text-dd-warm truncate">{TYPE_LABEL[c.type_constat]} · {fmt(c.date_constat)} · {c.locataire_sortant || c.locataire_entrant || "—"}{c.numero_dossier ? ` · ${c.numero_dossier}` : ""}</div>
                {render && <div className="mt-0.5">{render(c)}</div>}
              </div>
              <ArrowRight size={16} className="text-dd-blue shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-lg border border-dd-light px-4 py-3" data-testid={testid}>
      <div className="text-xs text-dd-warm">{label}</div>
      <div className="text-2xl font-semibold text-dd-blue tabular-nums">{value ?? 0}</div>
    </div>
  );
}
